/**
 * Prazo na tela da Central de Solicitações (#6351 — subtarefas #6353, #6355,
 * #6356 e #6357). Decisões do Victor, 20/10/2026:
 *
 *   - o prazo é uma data; vence às 18h (Brasília) daquele dia;
 *   - contagem "faltam 10h" / "5min" / "atrasada há 2h"; concluída mostra
 *     "no prazo" ou "atrasou Xd" em cinza;
 *   - cores: urgência vermelha com menos de 4h; alta vermelha com menos de 1
 *     dia; média amarela com menos de 2 dias e vermelha com menos de 1;
 *     atrasada sempre vermelha piscando;
 *   - rejeitadas e as que estão bloqueadas no momento ("depende de") ficam
 *     fora dos cálculos; subtarefas contam como hoje;
 *   - avisos ao responsável: 2 dias úteis, 1 dia útil e no dia (popup, uma vez
 *     por dia) e, para urgência e alta, popup nas últimas 2h e quando atrasada.
 *
 * Função pura, sem I/O: roda sob `node --test` com type-stripping.
 */

import type { TarefaPrioridade } from '../../types.ts';
import { ehDiaUtil } from './reguaPrazosAgrupamento.ts';

export const HORA_LIMITE_PRAZO = 18;
const MIN = 60_000;
const HORA = 60 * MIN;
const DIA = 24 * HORA;

/** Instante em que o prazo vence: 18h de Brasília (UTC-3, sem horário de verão) do dia. */
export function limiteDoPrazo(vencimento: string): Date {
  return new Date(`${vencimento.slice(0, 10)}T${String(HORA_LIMITE_PRAZO).padStart(2, '0')}:00:00-03:00`);
}

export interface SolicitacaoParaPrazo {
  vencimento?: string | null;
  prioridade?: TarefaPrioridade | string | null;
  solicitacao_status?: string | null;
  data_conclusao?: string | null;
  bloqueada_por_pessoa_id?: string | null;
  bloqueada_por_texto?: string | null;
}

export const estaBloqueada = (t: SolicitacaoParaPrazo) =>
  !!t.bloqueada_por_pessoa_id || !!String(t.bloqueada_por_texto ?? '').trim();

export const estaEncerrada = (t: SolicitacaoParaPrazo) =>
  t.solicitacao_status === 'concluido' || t.solicitacao_status === 'rejeitado';

/**
 * Entra nos cálculos (painéis, auditoria, análises)? Rejeitada não; bloqueada
 * só fica de fora enquanto estiver bloqueada (#6353).
 */
export const contaNosCalculos = (t: SolicitacaoParaPrazo) =>
  // Concluída com o "depende de" ainda marcado conta: o bloqueio ficou para trás.
  t.solicitacao_status !== 'rejeitado' && (t.solicitacao_status === 'concluido' || !estaBloqueada(t));

/** "2d 4h", "10h", "35min", "1min". */
export function duracaoCurta(ms: number): string {
  const m = Math.max(1, Math.round(Math.abs(ms) / MIN));
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  const resto = h % 24;
  return resto ? `${d}d ${resto}h` : `${d}d`;
}

export type NivelPrazo = 'sem_prazo' | 'pausada' | 'ok' | 'atencao' | 'critico' | 'atrasada' | 'entregue_no_prazo' | 'entregue_atrasada';

export interface SituacaoPrazo {
  nivel: NivelPrazo;
  texto: string;
  /** ms até o limite (negativo = atrasada). `null` sem prazo. */
  restanteMs: number | null;
}

/** Vermelho e amarelo por prioridade, em ms antes do limite. */
const LIMIARES: Record<string, { critico: number; atencao?: number }> = {
  urgencia: { critico: 4 * HORA },
  alta: { critico: DIA },
  media: { critico: DIA, atencao: 2 * DIA },
  baixa: { critico: 0 },
};

export function situacaoDoPrazo(t: SolicitacaoParaPrazo, agora: Date): SituacaoPrazo {
  if (!t.vencimento) return { nivel: 'sem_prazo', texto: 'Sem prazo', restanteMs: null };
  const limite = limiteDoPrazo(t.vencimento).getTime();

  if (t.solicitacao_status === 'rejeitado') return { nivel: 'sem_prazo', texto: 'Rejeitada', restanteMs: null };
  if (t.solicitacao_status === 'concluido') {
    const fim = t.data_conclusao ? new Date(t.data_conclusao).getTime() : agora.getTime();
    // Atraso de entrega em dias corridos a partir do limite; mesmo dia depois das 18h já é atraso.
    if (fim <= limite) return { nivel: 'entregue_no_prazo', texto: 'Entregue no prazo', restanteMs: null };
    const dias = Math.max(1, Math.ceil((fim - limite) / DIA));
    return { nivel: 'entregue_atrasada', texto: `Atrasou ${dias}d`, restanteMs: null };
  }
  // Bloqueada ("depende de") não corre prazo: "Pausada" até tirarem o bloqueio (#6539).
  if (estaBloqueada(t)) return { nivel: 'pausada', texto: 'Pausada', restanteMs: null };

  const restante = limite - agora.getTime();
  if (restante <= 0) return { nivel: 'atrasada', texto: `Atrasada há ${duracaoCurta(restante)}`, restanteMs: restante };
  const lim = LIMIARES[String(t.prioridade)] ?? LIMIARES.media;
  const nivel: NivelPrazo = restante < lim.critico ? 'critico' : lim.atencao && restante < lim.atencao ? 'atencao' : 'ok';
  return { nivel, texto: `Faltam ${duracaoCurta(restante)}`, restanteMs: restante };
}

/** Classes Tailwind do selo de prazo (claro e escuro). */
export const CLASSE_NIVEL: Record<NivelPrazo, string> = {
  sem_prazo: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  pausada: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  ok: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  atencao: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  critico: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  atrasada: 'bg-red-600 text-white motion-safe:animate-pulse',
  entregue_no_prazo: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  entregue_atrasada: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

// ------------------------------------------------------------------ avisos

const somaDia = (iso: string, n: number) => {
  const [a, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, d + n)).toISOString().slice(0, 10);
};

/** Dia (YYYY-MM-DD) de Brasília. */
export const diaEmBrasilia = (agora: Date) => agora.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });

/** Dias úteis de `hoje` até `vencimento` (0 = vence hoje; negativo = já passou). */
export function diasUteisAte(hoje: string, vencimento: string): number {
  const v = vencimento.slice(0, 10);
  if (v === hoje) return 0;
  if (v < hoje) return -1;
  let n = 0;
  for (let d = somaDia(hoje, 1); d <= v; d = somaDia(d, 1)) if (ehDiaUtil(d)) n++;
  // Prazo no fim de semana/feriado seguinte (sexta → sábado) não é "hoje": é o próximo dia útil.
  return Math.max(n, 1);
}

export type TipoAviso = 'dois_dias' | 'um_dia' | 'hoje' | 'aperto';

/**
 * Que aviso a solicitação do responsável pede agora? `null` se nenhum.
 * - "aperto": urgência/alta com 2h ou menos, ou atrasada — popup a cada 30 min;
 * - "hoje" / "um_dia" / "dois_dias": em dia útil, uma vez por dia.
 * Encerrada ou bloqueada não avisa.
 */
export function avisoDoPrazo(t: SolicitacaoParaPrazo, agora: Date): TipoAviso | null {
  if (!t.vencimento || estaEncerrada(t) || estaBloqueada(t)) return null;
  const restante = limiteDoPrazo(t.vencimento).getTime() - agora.getTime();
  if ((t.prioridade === 'urgencia' || t.prioridade === 'alta') && restante <= 2 * HORA) return 'aperto';
  const hoje = diaEmBrasilia(agora);
  if (!ehDiaUtil(hoje)) return null;
  const dias = diasUteisAte(hoje, t.vencimento);
  return dias === 0 && restante > 0 ? 'hoje' : dias === 1 ? 'um_dia' : dias === 2 ? 'dois_dias' : null;
}
